import React, { useState } from "react";
import { ArrowLeft, Check, Upload, KeyRound } from "lucide-react";
import WebcamCapture from "./WebcamCapture";
import { deviceApi } from "../lib/api";

/**
 * FaceEnrollmentFlow — Presence-side face enrollment.
 *
 * Truth contract:
 *   Presence captures images and a code.
 *   People Engine owns the employee↔face mapping and decides face status.
 *   Presence never says "Face verified". Pilot wording is honest:
 *     "Face captured", "Face profile enrolled".
 *
 * Steps:
 *   1. CODE   — user enters the 6-char enrollment code shown in People Engine
 *   2. FRONT  — capture front-facing photo
 *   3. LEFT   — capture left-profile photo
 *   4. RIGHT  — capture right-profile photo
 *   5. UPLOAD — submit all 3 captures + code to /api/presence/face-enrollments
 *   6. DONE   — show "Face profile enrolled" + employee name
 */
const STEPS = ["CODE", "FRONT", "LEFT", "RIGHT", "UPLOAD", "DONE"];

const ANGLE_COPY = {
  FRONT: {
    prompt: "Look straight into the camera",
    label: "Front",
    instr: "Face the camera. Neutral expression. Eyes open.",
  },
  LEFT: {
    prompt: "Turn your head slightly to your left",
    label: "Left profile",
    instr: "Keep your shoulders square; only turn your head.",
  },
  RIGHT: {
    prompt: "Turn your head slightly to your right",
    label: "Right profile",
    instr: "Keep your shoulders square; only turn your head.",
  },
};

export default function FaceEnrollmentFlow({ onClose }) {
  const [step, setStep] = useState("CODE");
  const [code, setCode] = useState("");
  const [captures, setCaptures] = useState({ front: null, left: null, right: null });
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const onCode = (e) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      setError("Enter the code shown in People Engine.");
      return;
    }
    setError("");
    setStep("FRONT");
  };

  const onAngleCaptured = (angleKey) => (dataUrl) => {
    setCaptures((c) => ({ ...c, [angleKey]: dataUrl }));
    if (angleKey === "front") setStep("LEFT");
    else if (angleKey === "left") setStep("RIGHT");
    else setStep("UPLOAD");
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    setProgress(10);
    try {
      const payload = {
        enrollment_code: code.trim().toUpperCase(),
        captures: [
          { image_base64: captures.front, angle: "front" },
          { image_base64: captures.left, angle: "left" },
          { image_base64: captures.right, angle: "right" },
        ],
        device_metadata: {
          captured_at: new Date().toISOString(),
          user_agent: navigator.userAgent.slice(0, 80),
        },
      };
      setProgress(40);
      const r = await deviceApi.post(
        "/presence/face-enrollments",
        payload,
        {
          onUploadProgress: (ev) => {
            if (ev.total) {
              const pct = Math.min(95, 40 + Math.round((ev.loaded / ev.total) * 55));
              setProgress(pct);
            }
          },
        },
      );
      setProgress(100);
      setResult(r.data);
      setStep("DONE");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg = typeof detail === "string" ? detail : "Could not save face profile. Try again.";
      // Map by server `detail` text first (more specific), then by HTTP status
      // (fallback). Keeps copy precise for the operator.
      const detailStr = (typeof detail === "string" ? detail : "").toLowerCase();
      if (detailStr.includes("invalid code")) {
        msg = "Enrollment code not found or expired. Please generate a new code in People Engine.";
      } else if (detailStr.includes("employee not found")) {
        msg = "This employee is not synced to Presence yet. Please ask admin to sync the employee from People Engine.";
      } else if (detailStr.includes("different outlet")) {
        msg = "This code belongs to a different outlet. Pair this device to the correct outlet.";
      } else if (detailStr.includes("enrollment code expired")
                 || detailStr.includes("code expired")) {
        msg = "Enrollment code expired. Please generate a new code in People Engine.";
      } else if (detailStr.includes("people engine did not accept")) {
        msg = "People Engine did not accept this enrollment. The code is still valid — check the code in People Engine and try again.";
      } else if (detailStr.includes("employee is not active")) {
        msg = "That employee is not active in People Engine.";
      } else if (status === 404) {
        msg = "Enrollment code not found or expired. Please generate a new code in People Engine.";
      } else if (status === 410) {
        msg = "This enrollment code is no longer valid. Please generate a new code in People Engine.";
      } else if (status === 403) {
        msg = "This code belongs to a different outlet. Pair this device to the correct outlet.";
      } else if (status === 413) {
        msg = "Captured images are too large. Try again with better lighting.";
      } else if (status === 409) {
        msg = "That employee is not active in People Engine.";
      }
      setError(msg);
      setStep("UPLOAD");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kiosk-root min-h-screen bg-white" data-testid="enroll-root">
      <div className="flex items-center h-14 px-6 border-b border-neutral-200">
        <button
          onClick={onClose}
          data-testid="enroll-back"
          className="mono-label inline-flex items-center gap-2 text-neutral-600 hover:text-black"
        >
          <ArrowLeft size={14} /> Back to capture
        </button>
        <span className="ml-auto mono-label text-neutral-500" data-testid="enroll-step-label">
          STEP {Math.max(1, STEPS.indexOf(step))} / 5 · FACE ENROLLMENT
        </span>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {step === "CODE" && (
          <form onSubmit={onCode} data-testid="enroll-code-form">
            <div className="mono-label">STEP 01 / 05 · ENROLLMENT CODE</div>
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-3 leading-[1]">
              Enrol a face profile.
            </h1>
            <p className="mt-4 text-neutral-700 max-w-lg">
              Ask the employee&apos;s manager to generate an enrollment code in
              People Engine, then enter it below.
            </p>
            <label className="mono-label mt-10 block">ENROLLMENT CODE</label>
            <input
              data-testid="enroll-code-input"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))
              }
              autoFocus
              maxLength={6}
              placeholder="ABC123"
              className="mt-2 w-full max-w-md h-20 px-5 border-2 border-black bg-white font-mono text-4xl tracking-[0.4em] uppercase focus:outline-none"
            />
            {error && (
              <div className="mt-4 text-red-700 font-mono text-sm" data-testid="enroll-error">
                {error}
              </div>
            )}
            <p className="mt-6 text-sm text-neutral-600 max-w-lg">
              Presence only captures the photos. The employee↔face mapping is
              owned by People Engine.
            </p>
            <button
              type="submit"
              data-testid="enroll-code-submit"
              disabled={code.length < 4}
              className="mt-8 h-14 px-8 bg-black text-white font-heading font-bold tracking-wide uppercase border border-black hover:bg-neutral-800 inline-flex items-center gap-3 disabled:opacity-40"
            >
              <KeyRound size={18} /> Begin face capture
            </button>
          </form>
        )}

        {(step === "FRONT" || step === "LEFT" || step === "RIGHT") && (
          <AngleStep
            angle={step}
            stepIndex={STEPS.indexOf(step)}
            onCapture={onAngleCaptured(step.toLowerCase())}
            onCancel={onClose}
          />
        )}

        {step === "UPLOAD" && (
          <UploadStep
            captures={captures}
            submitting={submitting}
            progress={progress}
            error={error}
            onSubmit={submit}
            onRestart={() => {
              setCaptures({ front: null, left: null, right: null });
              setStep("FRONT");
            }}
            onClose={onClose}
          />
        )}

        {step === "DONE" && result && (
          <DoneStep result={result} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function AngleStep({ angle, stepIndex, onCapture, onCancel }) {
  const copy = ANGLE_COPY[angle];
  return (
    <div data-testid={`enroll-angle-${angle.toLowerCase()}`}>
      <div className="mono-label">STEP 0{stepIndex} / 05 · {copy.label.toUpperCase()}</div>
      <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-3 leading-[1.05]">
        {copy.prompt}.
      </h2>
      <p className="mt-3 text-neutral-700 max-w-xl">{copy.instr}</p>
      <div className="mt-8">
        <WebcamCapture
          key={`enroll-cam-${angle.toLowerCase()}`}
          prompt={copy.prompt}
          actionLabel={`Capture ${copy.label.toLowerCase()}`}
          onCapture={onCapture}
          onCancel={onCancel}
          testIdPrefix={`enroll-cam-${angle.toLowerCase()}`}
        />
      </div>
    </div>
  );
}

function UploadStep({ captures, submitting, progress, error, onSubmit, onRestart, onClose }) {
  const all = ["front", "left", "right"];
  return (
    <div data-testid="enroll-upload-step">
      <div className="mono-label">STEP 04 / 05 · CONFIRM &amp; UPLOAD</div>
      <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-3 leading-[1.05]">
        Review captures.
      </h2>
      <p className="mt-3 text-neutral-700 max-w-xl">
        Upload these three captures to People Engine. People Engine stores
        them as the employee&apos;s face profile.
      </p>

      <div className="grid grid-cols-3 gap-4 mt-8 max-w-3xl">
        {all.map((a) => (
          <div key={a} className="border border-neutral-200">
            {captures[a] ? (
              <img
                src={captures[a]}
                alt={a}
                data-testid={`enroll-thumb-${a}`}
                className="w-full aspect-square object-cover"
              />
            ) : (
              <div className="w-full aspect-square bg-neutral-100" />
            )}
            <div className="mono-label py-2 text-center bg-[#FAFAFA]">{a.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {submitting && (
        <div className="mt-8 max-w-md" data-testid="enroll-progress">
          <div className="mono-label">UPLOADING · {progress}%</div>
          <div className="mt-2 h-2 bg-neutral-200 overflow-hidden">
            <div
              className="h-full bg-black transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {error && !submitting && (
        <div className="mt-6 text-red-700 font-mono text-sm" data-testid="enroll-upload-error">
          {error}
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="enroll-submit-btn"
          className="h-14 px-8 bg-black text-white font-heading font-bold tracking-wide uppercase border border-black hover:bg-neutral-800 inline-flex items-center gap-3 disabled:opacity-40"
        >
          <Upload size={18} /> {submitting ? "Uploading…" : "Send to People Engine"}
        </button>
        <button
          type="button"
          onClick={onRestart}
          disabled={submitting}
          data-testid="enroll-restart-btn"
          className="h-14 px-6 border border-neutral-900 bg-white font-mono text-sm uppercase tracking-wider disabled:opacity-40"
        >
          Retake all
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          data-testid="enroll-cancel-btn"
          className="h-14 px-6 border border-neutral-300 font-mono text-sm uppercase tracking-wider disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function DoneStep({ result, onClose }) {
  return (
    <div data-testid="enroll-done-step">
      <div className="border-2 border-[#22C55E] bg-[#DCFCE7] p-8 md:p-10 max-w-3xl">
        <div className="flex items-center gap-3 text-[#166534]">
          <Check size={22} />
          <span className="mono-label text-[#166534]">FACE PROFILE ENROLLED</span>
        </div>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-4 leading-[1]">
          {result.employee_name}.
        </h2>
        <p className="mt-4 text-neutral-800">
          {result.images_stored} captures stored in People Engine. The face
          profile is now linked to this employee.
        </p>
        <p className="mt-2 text-sm text-neutral-600">
          Identity decisions still happen in People Engine — this device only
          captures and uploads.
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="enroll-done-btn"
          className="mt-8 h-14 px-8 bg-black text-white font-mono text-sm uppercase tracking-wider"
        >
          Back to capture
        </button>
      </div>
    </div>
  );
}
